/* src/components/widget/Pages/Pages.tsx */
import { ComponentProps, ComponentChildren } from 'preact';
import { BackBar } from '../BackBar/BackBar';
import { useJourneyWidgetBackAction } from '../JourneyWidgetBackContext';
import styles from './Pages.module.css';

interface JourneyWidgetPageViewProps extends ComponentProps<'section'> {
  children?: ComponentChildren;
  variant?: 'onBrand' | 'onWhite';
  padding?: 'default' | 'flush';
}

interface PagePaddingProps extends ComponentProps<'div'> {
  children?: ComponentChildren;
}

/**
 * Shared surface wrapper used by all journey-widget page kinds.
 * @param param0
 * @returns
 */
export function JourneyWidgetPageView({
  children,
  variant = 'onBrand',
  padding = 'default',
  className = '',
  ...props
}: JourneyWidgetPageViewProps) {
  const paddingClassName =
    padding === 'flush' ? styles.flushPadding : styles.defaultPadding;
  const combinedClassName =
    `${styles.page} ${styles[variant]} ${paddingClassName} ${className}`.trim();

  return (
    <section
      className={combinedClassName}
      data-surface-variant={variant}
      {...props}
    >
      {children}
    </section>
  );
}

/**
 * Restores any remaining shared page inset inside flush page layouts.
 * @param param0
 * @returns
 */
export function PagePadding({
  children,
  className = '',
  ...props
}: PagePaddingProps) {
  const combinedClassName = `${styles.pagePadding} ${className}`.trim();

  return (
    <div className={combinedClassName} {...props}>
      {children}
    </div>
  );
}

/**
 * Search pages are the root page for each activity stack.
 * @param props
 * @returns
 */
export function SearchPage(props: JourneyWidgetPageViewProps) {
  return <JourneyWidgetPageView data-page-kind="search" {...props} />;
}

/**
 * Results pages keep the shared page shell but switch to the white surface.
 * @param props
 * @returns
 */
export function ResultsPage(props: JourneyWidgetPageViewProps) {
  const { className = '', ...restProps } = props;
  const combinedClassName = `${styles.boundedHeight} ${className}`.trim();

  return (
    <JourneyWidgetPageView
      data-page-kind="results"
      variant="onWhite"
      className={combinedClassName}
      {...restProps}
    />
  );
}

/**
 * Details pages render the shared BackBar when a tab-specific back action is
 * available via JourneyWidgetBackContext.
 * @param param0
 * @returns
 */
export function DetailsPage({
  children,
  padding = 'flush',
  className = '',
  ...props
}: JourneyWidgetPageViewProps) {
  const onBack = useJourneyWidgetBackAction();
  const combinedClassName = `${styles.boundedHeight} ${className}`.trim();

  return (
    <>
      {onBack && <BackBar onBack={onBack} />}
      <JourneyWidgetPageView
        data-page-kind="details"
        variant="onWhite"
        padding={padding}
        className={combinedClassName}
        {...props}
      >
        {children}
      </JourneyWidgetPageView>
    </>
  );
}
